/**
 * Approval API Service
 *
 * @fileoverview API client functions for approval workflow operations
 *
 * @changeHistory
 * - 2025-11-10: Initial creation - Approval workflow API client (AI Agent)
 * - 2026-07-07: Added shared CDA detail query options for breadcrumb labels (Codex)
 */

import {
  queryOptions,
  type DataTag,
  type UndefinedInitialDataOptions,
} from '@tanstack/react-query';
import { apiClient } from './api-client';

/**
 * Type definitions
 */

export interface PendingCDA {
  id: string;
  submission_date: string;
  agent_name: string;
  agent_email: string | null;
  property_address: string;
  purchase_price: string;
  total_commission_due: string;
  net_agent_commission: string;
  status: 'pending' | 'approved' | 'rejected' | 'canceled';
}

export interface CDADetails extends PendingCDA {
  user_id: string;
  agent_id: string | null;
  submittedBy?: {
    userId: string;
    name: string;
    role: 'agent' | 'broker' | 'admin' | 'transaction_coordinator';
  };
  version: number;
  submission_source: string;
  property_class?: 'residential' | 'commercial';
  agent_phone: string | null;
  property_city: string | null;
  property_state: string | null;
  property_zip: string | null;
  mls_number: string | null;
  contract_date: string | null;
  expires: string | null;
  representation: 'buyer' | 'seller' | 'tenant' | 'landlord' | null;
  lease_commission_basis: 'monthly' | 'annual' | null;
  seller_agent: string | null;
  buyer_agent: string | null;
  escrow_number: string | null;
  escrow_company: string | null;
  escrow_address: string | null;
  escrow_phone: string | null;
  escrow_fax: string | null;
  escrow_agent: string | null;
  escrow_agent_email: string | null;
  titleAndEscrow?: {
    companyName: string | null;
    escrowNumber: string | null;
    address: string | null;
    phone: string | null;
    fax: string | null;
    agentName: string | null;
    agentEmail: string | null;
  };
  commission_rate: string | null;
  commission_flat_fee: string | null;
  broker_transaction_fee: string | null;
  eo_insurance_fee: string | null;
  annual_dues: string | null;
  dues_payment_details: Array<{
    month: string;
    amount: number;
    type?: string;
  }> | null;
  dues_deduction_option: 'current_balance' | 'anniversary' | 'none' | null;
  dues_deduction_months: Array<{
    month: string;
    amount: number;
  }> | null;
  other_deductions: string | null;
  other_deductions_description: string | null;
  total_office_fees: string;
  agent_commission_percentage: string | null;
  bonus_amount: string | null;
  bonus_description: string | null;
  referring_broker_name: string | null;
  referring_broker_company: string | null;
  referring_broker_email: string | null;
  referring_broker_fee: string | null;
  agent_credit_applied_to: string | null;
  agent_credit_amount: string | null;
  agent_credit_acknowledgement: boolean | null;
  approved_by: string | null;
  approved_by_email: string | null;
  approved_at: string | null;
  approval_signature_data: string | null;
  rejected_by: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  rejection_comment: string | null;
  canceled_by: string | null;
  canceled_at: string | null;
  cancelation_reason: string | null;
  deposit_released_to_brokerage: string | null;
  audit_logs?: AuditLogEntry[];
  extendedFields?: CDADetailExtendedFields;
}

export interface CDADetailExtendedFields {
  transaction: {
    mlsNumber: string | null;
    contractDate: string | null;
    expires: string | null;
    representation: 'buyer' | 'seller' | 'tenant' | 'landlord' | null;
    leaseCommissionBasis: 'monthly' | 'annual' | null;
    sellerAgent: string | null;
    buyerAgent: string | null;
  };
  escrow: {
    escrowNumber: string | null;
    escrowCompany: string | null;
    escrowAddress: string | null;
    escrowPhone: string | null;
    escrowFax: string | null;
    escrowAgent: string | null;
    escrowAgentEmail: string | null;
  };
  financial: {
    depositReleasedToBrokerage: string | null;
  };
}

export interface AuditLogEntry {
  id: string;
  cda_id: string;
  action: string;
  performed_by: string;
  performed_at: string;
  notes: string | null;
}

export interface PendingApprovalsResponse {
  status: 'success';
  message: string;
  data: {
    cdas: PendingCDA[];
    pagination: {
      limit: number;
      offset: number;
      count: number;
    };
  };
}

export interface CDADetailsResponse {
  status: 'success';
  message: string;
  data: {
    cda: CDADetails;
  };
}

type CDADetailsQueryKey = readonly ['cda', string];
type CDADetailsQueryOptions = UndefinedInitialDataOptions<
  CDADetailsResponse,
  Error,
  CDADetailsResponse,
  CDADetailsQueryKey
> & {
  queryKey: DataTag<CDADetailsQueryKey, CDADetailsResponse, Error>;
};

export interface ApprovalResponse {
  status: 'success';
  message: string;
  data: {
    cda: {
      id: string;
      status: string;
      agent_name: string;
      property_address: string;
      net_agent_commission: string;
      approved_by: string | null;
      approved_at: string | null;
    };
    validation?: {
      warnings: string[];
    };
  };
}

export interface RejectionResponse {
  status: 'success';
  message: string;
  data: {
    cda: {
      id: string;
      status: string;
      agent_name: string;
      property_address: string;
      rejected_by: string | null;
      rejected_at: string | null;
      rejection_reason: string | null;
      rejection_comment: string | null;
    };
  };
}

export interface CancellationResponse {
  status: 'success';
  message: string;
  data: {
    cda: {
      id: string;
      status: string;
      agent_name: string;
      property_address: string;
      canceled_by: string | null;
      canceled_at: string | null;
      cancelation_reason: string | null;
    };
  };
}

export interface GetPendingApprovalsParams {
  agentName?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

export interface ApproveRequest {
  signatureData?: string;
}

export interface RejectRequest {
  rejectionReason: string;
  rejectionComment?: string;
}

export interface CancelRequest {
  cancelationReason: string;
}

/**
 * Approval API client
 */
export const approvalApi = {
  /**
   * Get pending CDAs for broker approval dashboard
   */
  getPendingApprovals: async (
    params: GetPendingApprovalsParams = {}
  ): Promise<PendingApprovalsResponse> => {
    const queryParams = new URLSearchParams();

    if (params.agentName) {
      queryParams.append('agentName', params.agentName);
    }
    if (params.dateFrom) {
      queryParams.append('dateFrom', params.dateFrom);
    }
    if (params.dateTo) {
      queryParams.append('dateTo', params.dateTo);
    }
    if (params.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params.offset !== undefined) {
      queryParams.append('offset', params.offset.toString());
    }

    const queryString = queryParams.toString();
    const endpoint = `/approvals/pending${queryString ? `?${queryString}` : ''}`;

    return apiClient.get<PendingApprovalsResponse>(endpoint);
  },

  /**
   * Get CDA details by ID
   */
  getCDADetails: async (cdaId: string): Promise<CDADetailsResponse> =>
    apiClient.get<CDADetailsResponse>(`/cdas/${cdaId}`),

  /**
   * Approve a CDA
   */
  approveCDA: async (cdaId: string, request: ApproveRequest = {}): Promise<ApprovalResponse> =>
    apiClient.patch<ApprovalResponse>(`/cdas/${cdaId}/approve`, request),

  /**
   * Reject a CDA
   */
  rejectCDA: async (cdaId: string, request: RejectRequest): Promise<RejectionResponse> =>
    apiClient.patch<RejectionResponse>(`/cdas/${cdaId}/reject`, request),

  /**
   * Cancel a CDA
   */
  cancelCDA: async (cdaId: string, request: CancelRequest): Promise<CancellationResponse> =>
    apiClient.patch<CancellationResponse>(`/cdas/${cdaId}/cancel`, request),
};

export const getCDADetailsQueryOptions = (cdaId: string): CDADetailsQueryOptions =>
  queryOptions<CDADetailsResponse, Error, CDADetailsResponse, CDADetailsQueryKey>({
    queryKey: ['cda', cdaId],
    queryFn: async () => approvalApi.getCDADetails(cdaId),
  });
